import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TwoFactorAuth from "@/components/TwoFactorAuth";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { updateMyProfile } from "@/lib/apiClient";
import { COUNTRY_NAMES as COUNTRIES, foldCountry } from "@/lib/countries";
import { User, Mail, Phone, MapPin, Globe, Calendar, Lock, Save, ChevronDown, Search, X } from "lucide-react";

const AGE_GROUP_KEYS = [
  ["Under 18", "profile.age_under_18"],
  ["18-24", "profile.age_18_24"],
  ["25-34", "profile.age_25_34"],
  ["35-44", "profile.age_35_44"],
  ["45-54", "profile.age_45_54"],
  ["55+", "profile.age_55_plus"],
];
const GENDER_KEYS = [
  ["Male", "profile.gender_male"],
  ["Female", "profile.gender_female"],
  ["Prefer not to say", "profile.gender_prefer_not_to_say"],
];


const inputCls = "w-full px-4 py-2.5 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] text-sm text-[#1F2937] focus:outline-none focus:border-[#064E3B] transition placeholder:text-[#9CA3AF]";

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#4B5563] mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </label>
      {children}
    </div>
  );
}

function CountrySelect({ value, onChange }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Accent-insensitive so "Reunion" finds "Réunion" and "curacao" finds
  // "Curaçao" — same folding the shared list and the backend use.
  const filtered = query.trim()
    ? COUNTRIES.filter((c) => foldCountry(c).includes(foldCountry(query)))
    : COUNTRIES;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  const select = (country) => { onChange(country); setQuery(""); setOpen(false); };
  const clear = (e) => { e.stopPropagation(); onChange(""); setQuery(""); };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${inputCls} flex items-center justify-between text-left`}
      >
        <span className={value ? "text-[#1F2937]" : "text-[#9CA3AF]"}>
          {value || t("profile.select_country")}
        </span>
        <span className="flex items-center gap-1 ml-2 shrink-0">
          {value && (
            <X className="w-3.5 h-3.5 text-[#9CA3AF] hover:text-[#1F2937]" onClick={clear} />
          )}
          <ChevronDown className={`w-4 h-4 text-[#4B5563] transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-[#E7E0D2] bg-white shadow-[0_12px_40px_-10px_rgba(6,78,59,0.18)] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E7E0D2]">
            <Search className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("profile.search_country")}
              className="flex-1 text-sm text-[#1F2937] bg-transparent outline-none placeholder:text-[#9CA3AF]"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")}>
                <X className="w-3.5 h-3.5 text-[#9CA3AF] hover:text-[#1F2937]" />
              </button>
            )}
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-2.5 text-sm text-[#9CA3AF]">{t("profile.no_results")}</li>
            ) : (
              filtered.map((c) => (
                <li key={c}>
                  <button
                    type="button"
                    onClick={() => select(c)}
                    className={`w-full text-left px-4 py-2 text-sm transition ${
                      c === value ? "bg-[#ECFDF5] text-[#064E3B] font-medium" : "text-[#1F2937] hover:bg-[#F9F5EE]"
                    }`}
                  >
                    {c}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();

  const [info, setInfo] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    city: user?.city || "",
    country: user?.country || "",
    age_group: user?.age_group || "",
    gender: user?.gender || "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const handleInfoChange = (e) => setInfo((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handlePwdChange = (e) => setPwd((p) => ({ ...p, [e.target.name]: e.target.value }));

  const saveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const updated = await updateMyProfile({
        name: info.name,
        email: info.email,
        phone: info.phone,
        city: info.city,
        country: info.country,
        age_group: info.age_group || null,
        gender: info.gender || null,
      });
      setUser(updated);
      toast.success(t("profile.toast_updated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("profile.toast_save_failed"));
    } finally {
      setSavingInfo(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error(t("auth.toast_passwords_mismatch")); return; }
    setSavingPwd(true);
    try {
      await updateMyProfile({ current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success(t("profile.toast_password_updated"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("profile.toast_password_failed"));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-[768px] mx-auto w-full px-5 sm:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("profile.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">{t("profile.title")}</h1>
        <p className="text-[#4B5563] mt-3">{t("profile.subtitle")}</p>

        <section className="mt-8 rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-8">
          <h2 className="font-heading text-lg font-semibold text-[#1F2937] mb-6">{t("profile.personal_info")}</h2>
          <form onSubmit={saveInfo} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t("profile.full_name")} icon={User}>
              <input name="name" value={info.name} onChange={handleInfoChange}
                placeholder={t("auth.name_placeholder")} className={inputCls} required />
            </Field>

            <Field label={t("profile.email")} icon={Mail}>
              <input name="email" type="email" value={info.email} onChange={handleInfoChange}
                placeholder={t("auth.email_placeholder")} className={inputCls} required />
            </Field>

            <Field label={t("profile.phone")} icon={Phone}>
              <input name="phone" type="tel" value={info.phone} onChange={handleInfoChange}
                placeholder="+1 555 000 0000" className={inputCls} />
            </Field>

            <Field label={t("profile.age_group")} icon={Calendar}>
              <select name="age_group" value={info.age_group} onChange={handleInfoChange} className={inputCls}>
                <option value="">{t("profile.select_age_group")}</option>
                {AGE_GROUP_KEYS.map(([g, key]) => <option key={g} value={g}>{t(key)}</option>)}
              </select>
            </Field>

            <Field label={t("profile.gender")} icon={User}>
              <select name="gender" value={info.gender} onChange={handleInfoChange} className={inputCls}>
                <option value="">{t("profile.select_gender")}</option>
                {GENDER_KEYS.map(([g, key]) => <option key={g} value={g}>{t(key)}</option>)}
              </select>
            </Field>

            <Field label={t("profile.city")} icon={MapPin}>
              <input name="city" value={info.city} onChange={handleInfoChange}
                placeholder={t("profile.city")} className={inputCls} />
            </Field>

            <Field label={t("profile.country")} icon={Globe}>
              <CountrySelect value={info.country} onChange={(v) => setInfo((p) => ({ ...p, country: v }))} />
            </Field>

            <div className="sm:col-span-2 flex justify-end pt-2">
              <button type="submit" disabled={savingInfo}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 transition">
                <Save className="w-4 h-4" />
                {savingInfo ? t("profile.saving") : t("profile.save_changes")}
              </button>
            </div>
          </form>
        </section>

        <TwoFactorAuth />

        <section className="mt-6 rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-8">
          <h2 className="font-heading text-lg font-semibold text-[#1F2937] mb-6">{t("profile.change_password")}</h2>
          <form onSubmit={savePassword} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Field label={t("profile.current_password")} icon={Lock}>
                <input name="current_password" type="password" value={pwd.current_password}
                  onChange={handlePwdChange} placeholder={t("profile.current_password_placeholder")} className={inputCls} required />
              </Field>
            </div>

            <Field label={t("profile.new_password")} icon={Lock}>
              <input name="new_password" type="password" value={pwd.new_password}
                onChange={handlePwdChange} placeholder={t("profile.min_8_chars")} className={inputCls} required minLength={8} />
            </Field>

            <Field label={t("profile.confirm_password")} icon={Lock}>
              <input name="confirm" type="password" value={pwd.confirm}
                onChange={handlePwdChange} placeholder={t("profile.repeat_new_password")} className={inputCls} required />
            </Field>

            <div className="sm:col-span-2 flex justify-end pt-2">
              <button type="submit" disabled={savingPwd}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 transition">
                <Save className="w-4 h-4" />
                {savingPwd ? t("profile.saving") : t("profile.update_password")}
              </button>
            </div>
          </form>
        </section>
      </main>
      <Footer />
    </div>
  );
}
